import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from './Button';
import { Modal } from './Modal';

/**
 * Подтверждения действий — своим окном вместо системного.
 *
 * ЗАЧЕМ. Раньше в 29 местах стоял браузерный `confirm()`. В приложении из
 * магазина это окно операционной системы поверх нашего интерфейса: чужеродная
 * рамка, кнопки на языке системы, и главное — оно **останавливает всё
 * приложение** до ответа. В мок-экзамене с обратным отсчётом это особенно некстати.
 *
 * Здесь то же самое делает обычное модальное окно: наше оформление, наши
 * переводы, аппаратная кнопка «Назад» его закрывает (перехват встроен в `Modal`).
 *
 * КАК ПОЛЬЗОВАТЬСЯ — почти как прежде, только с ожиданием:
 *
 *     const ask = useConfirm();
 *     if (await ask({ message: t('adminStudents.confirmDelete') })) remove.mutate();
 *
 * Обещание разрешается `true` при подтверждении и `false` при отказе — в том
 * числе когда окно закрыли крестиком, нажатием мимо или кнопкой «Назад».
 * Поэтому вызывающий код не обязан различать способы отказа.
 */

export type ConfirmOptions = {
  message: string;
  title?: string;
  /** Надпись на кнопке подтверждения. По умолчанию — «Продолжить». */
  confirmLabel?: string;
  /** Надпись на кнопке отказа. По умолчанию — «Отмена»; у выхода из урока — «Остаться». */
  cancelLabel?: string;
  /** Опасное действие: кнопка красная. Для удаления и блокировки. */
  danger?: boolean;
  /**
   * Просто сообщение, выбора нет — замена браузерному `alert()`.
   * Кнопка одна, обещание всегда разрешается `true`.
   */
  notice?: boolean;
};

type AskFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<AskFn | null>(null);

// Файл отдаёт и компонент-провайдер, и хук к нему — так устроен любой провайдер,
// и разносить их по файлам значило бы разрывать одно целое ради правила о
// горячей перезагрузке.
// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): AskFn {
  const ask = useContext(ConfirmContext);
  if (!ask) {
    // Провайдер стоит в корне приложения; если его нет — это ошибка сборки
    // дерева, а не случайность, и молчать о ней нельзя.
    throw new Error('useConfirm вызван вне ConfirmProvider');
  }
  return ask;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  // Ответ отдаётся через сохранённую функцию разрешения: одно окно за раз,
  // повторный вызов до ответа перекрыл бы предыдущий — поэтому храним ссылку.
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const ask = useCallback<AskFn>((next) => {
    return new Promise<boolean>((resolve) => {
      // Если предыдущее окно почему-то ещё висит — отвечаем на него отказом,
      // чтобы вызывающий код не остался ждать вечно.
      resolveRef.current?.(false);
      resolveRef.current = resolve;
      setOpts(next);
    });
  }, []);

  const finish = (value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setOpts(null);
  };

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <Modal
        open={opts !== null}
        onClose={() => finish(false)}
        title={opts?.title ?? t('common.confirmTitle')}
        size="sm"
      >
        <p className="text-sm text-on-surface-variant">{opts?.message}</p>
        <div className="mt-5 flex gap-3">
          {!opts?.notice && (
            <Button variant="secondary" fullWidth onClick={() => finish(false)}>
              {opts?.cancelLabel ?? t('common.cancel')}
            </Button>
          )}
          <Button
            variant={opts?.danger ? 'danger' : 'primary'}
            fullWidth
            onClick={() => finish(true)}
          >
            {opts?.confirmLabel ?? (opts?.notice ? t('common.ok') : t('common.continue'))}
          </Button>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}
